import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { 
  BookOpen, Clock, Calendar, Users, CheckCircle, Loader2,
  GraduationCap, Heart, Phone, Mail, MapPin, FileText, CreditCard, ShoppingCart
} from 'lucide-react';
import { Button } from '../components/ui/button';
import SectionHeading from '../components/SectionHeading';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BOOKS_COST = 70;

const feeStructure = [
  { children: '1 Child',    monthly: 75,  term: 280, numChildren: 1 },
  { children: '2 Children', monthly: 110, term: 400, numChildren: 2 },
  { children: '3 Children', monthly: 140, term: 520, numChildren: 3 },
  { children: '4 Children', monthly: 160, term: 600, numChildren: 4 },
];

const curriculum = [
  { name: 'Hifz Memorization of Quran', description: 'Memorize and preserve the Holy Quran with proper guidance' },
  { name: 'Tajweed', description: 'Master the rules of Quranic pronunciation' },
  { name: 'General Islamic Studies', description: 'Learn about Islamic history, values, and practices' },
];

const requiredBooks = [
  'Noorani Qaida',
  'Quran',
  'I Love Islam Series',
  'Notebooks',
];

const EducationPage = () => {
  const [searchParams] = useSearchParams();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);
  const [paymentCompleted, setPaymentCompleted] = useState(false);
  const [paymentSessionId, setPaymentSessionId] = useState(null);
  const [tuitionThankYou, setTuitionThankYou] = useState(null); // { sessionId, loading, amount, currency, paid }
  const [hasOwnBooks, setHasOwnBooks] = useState(false);
  const [formData, setFormData] = useState({
    parentFirstName: '',
    parentLastName: '',
    address: '',
    parentCell: '',
    email: '',
    emergencyFirstName: '',
    emergencyLastName: '',
    emergencyRelationship: '',
    emergencyCell: '',
    children: [{ firstName: '', lastName: '', age: '' }],
    additionalNotes: '',
  });

  // Check for payment success on mount
  useEffect(() => {
    const paymentStatus = searchParams.get('payment');
    const tuitionPayment = searchParams.get('tuition_payment');
    const sessionId = searchParams.get('session_id');
    
    if (paymentStatus === 'success' && sessionId) {
      setPaymentCompleted(true);
      setPaymentSessionId(sessionId);
      toast.success('Payment successful! Please complete your registration below.');
      // Scroll to registration form
      setTimeout(() => {
        document.querySelector('[data-testid="education-registration"]')?.scrollIntoView({ behavior: 'smooth' });
      }, 500);
    } else if (paymentStatus === 'cancelled') {
      toast.info('Payment was cancelled. You can try again when ready.');
    }

    // Tuition payment flow — show Thank You screen
    if (tuitionPayment === 'success') {
      setTuitionThankYou({ sessionId: sessionId || '', loading: !!sessionId });
      if (sessionId) {
        axios.get(`${API}/donations/status/${sessionId}`)
          .then((res) => {
            const s = res.data || {};
            setTuitionThankYou({
              sessionId,
              loading: false,
              amount: s.amount_total ? s.amount_total / 100 : null,
              currency: (s.currency || 'cad').toUpperCase(),
              paid: s.payment_status === 'paid' || s.status === 'complete',
            });
          })
          .catch(() => setTuitionThankYou((p) => ({ ...(p || {}), loading: false })));
      }
    } else if (tuitionPayment === 'cancelled') {
      toast.info('Tuition payment was cancelled. You can try again when ready.');
    }
  }, [searchParams]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleChildChange = (index, field, value) => {
    setFormData(prev => {
      const newChildren = [...prev.children];
      newChildren[index] = { ...newChildren[index], [field]: value };
      return { ...prev, children: newChildren };
    });
  };

  const addChild = () => {
    setFormData(prev => ({
      ...prev,
      children: [...prev.children, { firstName: '', lastName: '', age: '' }]
    }));
  };

  const removeChild = (index) => {
    if (formData.children.length > 1) {
      setFormData(prev => ({
        ...prev,
        children: prev.children.filter((_, i) => i !== index)
      }));
    }
  };

  // Handle fee card click - redirect to checkout
  const handleFeeCheckout = async (fee) => {
    const numChildren = formData.children.filter(c => c.firstName && c.lastName && c.age).length;
    
    if (numChildren === 0) {
      toast.error('Please add at least one child with complete information first');
      // Scroll to children section
      document.querySelector('[data-testid="children-section"]')?.scrollIntoView({ behavior: 'smooth' });
      return;
    }

    // Calculate total: first month + books ($70 per child) if needed
    const monthlyFee = fee.monthly;
    const booksCost = hasOwnBooks ? 0 : BOOKS_COST * numChildren; // $70 per child only if books needed
    const totalAmount = monthlyFee + booksCost;

    setIsProcessingPayment(true);
    try {
      const response = await axios.post(`${API}/education/checkout`, {
        num_children: numChildren,
        monthly_fee: monthlyFee,
        books_cost: booksCost,
        total_amount: totalAmount,
        origin_url: window.location.origin
      });

      if (response.data.url) {
        window.location.href = response.data.url;
      }
    } catch (error) {
      console.error('Error creating checkout:', error);
      toast.error('Failed to create checkout. Please try again.');
    } finally {
      setIsProcessingPayment(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Require payment first
    if (!paymentCompleted) {
      toast.error('Please complete payment first by clicking on a fee option above');
      document.querySelector('[data-testid="fee-structure"]')?.scrollIntoView({ behavior: 'smooth' });
      return;
    }
    
    setIsSubmitting(true);

    try {
      await axios.post(`${API}/education/register`, {
        ...formData,
        paymentSessionId: paymentSessionId
      });
      setSubmitted(true);
      toast.success('Registration submitted successfully!');
    } catch (error) {
      console.error('Error submitting registration:', error);
      toast.error('Failed to submit registration. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Tuition Payment Thank-You screen
  if (tuitionThankYou) {
    return (
      <div className="min-h-screen pt-24 flex items-center justify-center px-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="max-w-2xl w-full text-center"
          data-testid="tuition-thank-you"
        >
          <div className="w-24 h-24 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="h-14 w-14 text-green-500" />
          </div>
          <h1 className="text-4xl font-bold font-['Outfit'] text-foreground mb-3">Thank You!</h1>
          <p className="text-lg text-muted-foreground mb-6">
            Your tuition payment has been received. Jazakum Allahu Khairan for supporting your child&apos;s Islamic education.
          </p>

          <div className="p-6 rounded-2xl bg-card border border-border text-left space-y-3 mb-8">
            <h3 className="text-lg font-semibold text-foreground flex items-center gap-2 mb-2">
              <CheckCircle className="h-5 w-5 text-green-500" /> Payment Confirmation
            </h3>
            {tuitionThankYou.loading ? (
              <p className="text-sm text-muted-foreground flex items-center gap-2">
                <Loader2 className="h-4 w-4 animate-spin" /> Verifying payment with Stripe…
              </p>
            ) : (
              <>
                {tuitionThankYou.amount != null && (
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Amount Paid</span>
                    <span className="text-foreground font-semibold">${tuitionThankYou.amount.toFixed(2)} {tuitionThankYou.currency}</span>
                  </div>
                )}
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Status</span>
                  <span className={`font-semibold ${tuitionThankYou.paid ? 'text-green-500' : 'text-amber-500'}`}>
                    {tuitionThankYou.paid ? 'Paid ✓' : 'Processing'}
                  </span>
                </div>
                {tuitionThankYou.sessionId && (
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Reference</span>
                    <span className="text-foreground font-mono text-xs">{tuitionThankYou.sessionId.slice(-12)}</span>
                  </div>
                )}
                <p className="text-xs text-muted-foreground pt-2 border-t border-border">
                  A payment receipt will be emailed to you shortly.
                </p>
              </>
            )}
          </div>

          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Button
              onClick={() => { setTuitionThankYou(null); window.history.replaceState({}, '', '/education'); }}
              className="rounded-full h-12 px-6 bg-primary text-primary-foreground hover:bg-primary/90"
              data-testid="back-to-kids-btn"
            >
              ← Back to Kids Classes
            </Button>
            <Button
              onClick={() => window.location.href = '/'}
              variant="outline"
              className="rounded-full h-12 px-6"
              data-testid="thank-you-home-btn"
            >
              Return Home
            </Button>
          </div>
        </motion.div>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen pt-24 flex items-center justify-center">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="max-w-md mx-auto text-center p-8"
        >
          <div className="w-20 h-20 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="h-10 w-10 text-green-500" />
          </div>
          <h1 className="text-3xl font-bold font-['Outfit'] text-foreground mb-4">
            Registration Received!
          </h1>
          <p className="text-muted-foreground mb-6">
            Thank you for registering your child(ren) for our Islamic Education Program. 
            We will contact you shortly with further details.
          </p>
          <Button onClick={() => window.location.href = '/'} className="rounded-full">
            Return Home
          </Button>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-24">
      {/* Class Resumption Banner */}
      <div className="bg-secondary/10 text-foreground py-3 px-4 border-b border-secondary/30">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-center gap-2 sm:gap-6">
          <div className="flex items-center gap-3">
            <Calendar className="h-5 w-5 text-secondary" />
            <p className="text-center font-semibold text-base sm:text-lg">
              Children Classes Resume <span className="text-secondary">Saturday, August 8th at 3:00 PM</span>
            </p>
          </div>
          <a href="/adult-education" className="text-sm px-4 py-1.5 rounded-full bg-primary/10 border border-primary/30 text-primary hover:bg-primary/20 transition-colors" data-testid="adult-classes-link">
            👉 Looking for Adult Classes?
          </a>
        </div>
      </div>

      {/* Hero */}
      <section className="py-16 lg:py-24 hero-gradient islamic-pattern">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-3xl"
          >
            <span className="inline-block px-4 py-1 rounded-full bg-primary/10 text-primary text-sm font-medium mb-4">
              Weekend Islamic School
            </span>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-foreground font-['Outfit'] leading-tight mb-6">
              Islamic <span className="text-gradient">Education Program</span>
            </h1>
            <p className="text-lg text-muted-foreground leading-relaxed">
              Nurture your children's Islamic knowledge with our weekend Quran and Islamic Studies classes.
              Saturday & Sunday sessions designed for young learners.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Program Details */}
      <section className="py-16 lg:py-24 bg-card border-y border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeading
            badge="Program Details"
            title="Weekend Classes"
            description="Comprehensive Islamic education for children"
          />

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
            {/* Schedule */}
            <div className="p-6 space-y-4 bg-card rounded-2xl border border-border hover:border-primary/50 transition-colors">
              <div className="flex items-center gap-3">
                <div className="p-3 rounded-xl bg-primary/10">
                  <Calendar className="h-6 w-6 text-primary" />
                </div>
                <h3 className="text-xl font-semibold font-['Outfit'] text-foreground">Schedule</h3>
              </div>
              <div className="space-y-2 text-muted-foreground">
                <p className="flex items-center gap-2">
                  <Clock className="h-4 w-4 text-primary" />
                  Saturday & Sunday
                </p>
                <p className="flex items-center gap-2">
                  <Clock className="h-4 w-4 text-primary" />
                  3:00 PM - 5:00 PM
                </p>
                <p className="flex items-center gap-2 text-secondary font-medium">
                  <Calendar className="h-4 w-4 text-secondary" />
                  Classes Resume: Saturday, August 8th at 3:00 PM
                </p>
              </div>
            </div>

            {/* Location */}
            <div className="p-6 space-y-4 bg-card rounded-2xl border border-border hover:border-primary/50 transition-colors">
              <div className="flex items-center gap-3">
                <div className="p-3 rounded-xl bg-secondary/10">
                  <MapPin className="h-6 w-6 text-secondary" />
                </div>
                <h3 className="text-xl font-semibold font-['Outfit'] text-foreground">Location</h3>
              </div>
              <div className="space-y-2 text-muted-foreground">
                <p>54 St. Paul Crescent</p>
                <p>Barrie, ON L9J 0M2</p>
              </div>
            </div>

            {/* Books */}
            <div className="p-6 space-y-4 bg-card rounded-2xl border border-border hover:border-primary/50 transition-colors">
              <div className="flex items-center gap-3">
                <div className="p-3 rounded-xl bg-green-500/10">
                  <BookOpen className="h-6 w-6 text-green-500" />
                </div>
                <h3 className="text-xl font-semibold font-['Outfit'] text-foreground">Required Books</h3>
              </div>
              <div className="space-y-1 text-muted-foreground">
                {requiredBooks.map((book, index) => (
                  <p key={index} className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-primary" />
                    {book}
                  </p>
                ))}
                <p className="mt-3 text-secondary font-semibold">Books Package: $70 (one-time per child)</p>
              </div>
            </div>
          </div>

          {/* Curriculum */}
          <div className="mb-12">
            <h3 className="text-2xl font-bold font-['Outfit'] text-foreground mb-6 text-center">Curriculum</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {curriculum.map((subject, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="p-6 rounded-2xl bg-background border border-border"
                >
                  <GraduationCap className="h-8 w-8 text-primary mb-4" />
                  <h4 className="text-lg font-semibold text-foreground mb-2">{subject.name}</h4>
                  <p className="text-muted-foreground text-sm">{subject.description}</p>
                </motion.div>
              ))}
            </div>
          </div>

          {/* Weekly Timetable */}
          <div className="mb-12" data-testid="weekly-timetable">
            <h3 className="text-2xl font-bold font-['Outfit'] text-foreground mb-6 text-center">Weekly Timetable</h3>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Saturday Schedule */}
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                className="p-6 rounded-2xl bg-card border border-primary/30"
              >
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-2 rounded-lg bg-primary/10">
                    <Calendar className="h-5 w-5 text-primary" />
                  </div>
                  <div>
                    <h4 className="text-lg font-semibold text-foreground">Saturday</h4>
                    <p className="text-sm text-primary font-medium">Hifz Focus</p>
                  </div>
                </div>
                <div className="space-y-3">
                  <div className="flex items-start gap-3 p-3 rounded-xl bg-background/50">
                    <span className="text-xs font-mono text-primary whitespace-nowrap mt-0.5">3:00 - 3:10</span>
                    <div>
                      <p className="font-medium text-foreground text-sm">Opening Session</p>
                      <p className="text-xs text-muted-foreground">Du'a, Reminder</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3 p-3 rounded-xl bg-background/50">
                    <span className="text-xs font-mono text-primary whitespace-nowrap mt-0.5">3:10 - 3:45</span>
                    <div>
                      <p className="font-medium text-foreground text-sm">Hifz (Memorization)</p>
                      <p className="text-xs text-muted-foreground">New lesson, repetition, testing</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3 p-3 rounded-xl bg-background/50">
                    <span className="text-xs font-mono text-primary whitespace-nowrap mt-0.5">3:45 - 4:15</span>
                    <div>
                      <p className="font-medium text-foreground text-sm">Qur'an Reading (Nazirah)</p>
                      <p className="text-xs text-muted-foreground">Fluency & pronunciation</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3 p-3 rounded-xl bg-background/50">
                    <span className="text-xs font-mono text-primary whitespace-nowrap mt-0.5">4:15 - 4:35</span>
                    <div>
                      <p className="font-medium text-foreground text-sm">Hifz Revision</p>
                      <p className="text-xs text-muted-foreground">Previous surahs, pair revision</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3 p-3 rounded-xl bg-background/50">
                    <span className="text-xs font-mono text-primary whitespace-nowrap mt-0.5">4:35 - 5:00</span>
                    <div>
                      <p className="font-medium text-foreground text-sm">Closing</p>
                      <p className="text-xs text-muted-foreground">Recitation, Du'a</p>
                    </div>
                  </div>
                </div>
              </motion.div>

              {/* Sunday Schedule */}
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                className="p-6 rounded-2xl bg-card border border-secondary/30"
              >
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-2 rounded-lg bg-secondary/10">
                    <Calendar className="h-5 w-5 text-secondary" />
                  </div>
                  <div>
                    <h4 className="text-lg font-semibold text-foreground">Sunday</h4>
                    <p className="text-sm text-secondary font-medium">Tajweed & Islamic Studies</p>
                  </div>
                </div>
                <div className="space-y-3">
                  <div className="flex items-start gap-3 p-3 rounded-xl bg-background/50">
                    <span className="text-xs font-mono text-secondary whitespace-nowrap mt-0.5">3:00 - 3:10</span>
                    <div>
                      <p className="font-medium text-foreground text-sm">Opening Session</p>
                      <p className="text-xs text-muted-foreground">Du'a, Reminder</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3 p-3 rounded-xl bg-background/50">
                    <span className="text-xs font-mono text-secondary whitespace-nowrap mt-0.5">3:10 - 3:40</span>
                    <div>
                      <p className="font-medium text-foreground text-sm">Tajweed Lesson</p>
                      <p className="text-xs text-muted-foreground">One rule + demonstration</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3 p-3 rounded-xl bg-background/50">
                    <span className="text-xs font-mono text-secondary whitespace-nowrap mt-0.5">3:40 - 4:10</span>
                    <div>
                      <p className="font-medium text-foreground text-sm">Tajweed Practice</p>
                      <p className="text-xs text-muted-foreground">Apply in Qur'an reading</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3 p-3 rounded-xl bg-background/50">
                    <span className="text-xs font-mono text-secondary whitespace-nowrap mt-0.5">4:10 - 4:40</span>
                    <div>
                      <p className="font-medium text-foreground text-sm">Islamic Studies</p>
                      <p className="text-xs text-muted-foreground">Aqeedah, Seerah, Fiqh, Akhlaq</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3 p-3 rounded-xl bg-background/50">
                    <span className="text-xs font-mono text-secondary whitespace-nowrap mt-0.5">4:40 - 4:55</span>
                    <div>
                      <p className="font-medium text-foreground text-sm">Revision & Quiz</p>
                      <p className="text-xs text-muted-foreground">Review and assessment</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3 p-3 rounded-xl bg-background/50">
                    <span className="text-xs font-mono text-secondary whitespace-nowrap mt-0.5">4:55 - 5:00</span>
                    <div>
                      <p className="font-medium text-foreground text-sm">Closing Du'a</p>
                      <p className="text-xs text-muted-foreground">End of session</p>
                    </div>
                  </div>
                </div>
              </motion.div>
            </div>
            <p className="text-center text-sm text-muted-foreground mt-4">All times are in EST</p>
          </div>
        </div>
      </section>

      {/* Academic Calendar Section */}
      <section className="py-16 lg:py-24 bg-card border-t border-border" data-testid="academic-calendar">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeading
            badge="Key Dates"
            title="Academic Calendar 2026"
            description="Important dates for our Islamic education program"
          />

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Term 1 */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="p-6 rounded-2xl bg-background border border-primary/30"
            >
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 rounded-lg bg-primary/10">
                  <Calendar className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <h3 className="font-semibold text-foreground">Term 1 - Spring</h3>
                  <p className="text-sm text-primary">April - June 2026</p>
                </div>
              </div>
              <div className="space-y-3 text-sm">
                <div className="flex justify-between py-2 border-b border-border">
                  <span className="text-muted-foreground">Term Starts</span>
                  <span className="text-foreground font-medium">April 4</span>
                </div>
                <div className="flex justify-between py-2 border-b border-border">
                  <span className="text-muted-foreground">Mid-Term Exams</span>
                  <span className="text-foreground font-medium">May 16-17</span>
                </div>
                <div className="flex justify-between py-2 border-b border-border">
                  <span className="text-muted-foreground">Final Exams</span>
                  <span className="text-foreground font-medium">June 20-21</span>
                </div>
                <div className="flex justify-between py-2">
                  <span className="text-muted-foreground">Term Ends</span>
                  <span className="text-foreground font-medium">June 28</span>
                </div>
              </div>
              <div className="mt-4 p-3 rounded-lg bg-primary/5 border border-primary/20">
                <p className="text-xs text-primary font-medium">Summer Break: June 29 - August 7</p>
              </div>
            </motion.div>

            {/* Term 2 */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="p-6 rounded-2xl bg-background border border-secondary/30"
            >
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 rounded-lg bg-secondary/10">
                  <Calendar className="h-5 w-5 text-secondary" />
                </div>
                <div>
                  <h3 className="font-semibold text-foreground">Term 2 - Fall</h3>
                  <p className="text-sm text-secondary">August - November 2026</p>
                </div>
              </div>
              <div className="space-y-3 text-sm">
                <div className="flex justify-between py-2 border-b border-border">
                  <span className="text-muted-foreground">Term Starts</span>
                  <span className="text-foreground font-medium">August 8</span>
                </div>
                <div className="flex justify-between py-2 border-b border-border">
                  <span className="text-muted-foreground">Mid-Term Exams</span>
                  <span className="text-foreground font-medium">October 3-4</span>
                </div>
                <div className="flex justify-between py-2 border-b border-border">
                  <span className="text-muted-foreground">Final Exams</span>
                  <span className="text-foreground font-medium">November 21-22</span>
                </div>
                <div className="flex justify-between py-2 border-b border-border">
                  <span className="text-muted-foreground">Quran Musabaqa</span>
                  <span className="text-foreground font-medium">November 28-29</span>
                </div>
                <div className="flex justify-between py-2">
                  <span className="text-muted-foreground">Term Ends</span>
                  <span className="text-foreground font-medium">November 29</span>
                </div>
              </div>
              <div className="mt-4 p-3 rounded-lg bg-secondary/5 border border-secondary/20">
                <p className="text-xs text-secondary font-medium">Winter Break: Nov 30 - Jan 2, 2027</p>
              </div>
            </motion.div>

            {/* Term 3 */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="p-6 rounded-2xl bg-background border border-blue-500/30"
            >
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 rounded-lg bg-blue-500/10">
                  <Calendar className="h-5 w-5 text-blue-400" />
                </div>
                <div>
                  <h3 className="font-semibold text-foreground">Term 3 - Winter</h3>
                  <p className="text-sm text-blue-400">January - February 2027</p>
                </div>
              </div>
              <div className="space-y-3 text-sm">
                <div className="flex justify-between py-2 border-b border-border">
                  <span className="text-muted-foreground">Term Starts</span>
                  <span className="text-foreground font-medium">January 3</span>
                </div>
                <div className="flex justify-between py-2 border-b border-border">
                  <span className="text-muted-foreground">Mid-Term Exams</span>
                  <span className="text-foreground font-medium">January 31 - Feb 1</span>
                </div>
                <div className="flex justify-between py-2 border-b border-border">
                  <span className="text-muted-foreground">Final Exams</span>
                  <span className="text-foreground font-medium">February 21-22</span>
                </div>
                <div className="flex justify-between py-2">
                  <span className="text-muted-foreground">Term Ends</span>
                  <span className="text-foreground font-medium">February 28</span>
                </div>
              </div>
              <div className="mt-4 p-3 rounded-lg bg-blue-500/5 border border-blue-500/20">
                <p className="text-xs text-blue-400 font-medium">Ramadan Break: March 2027</p>
              </div>
            </motion.div>
          </div>

          <div className="mt-8 text-center">
            <p className="text-sm text-muted-foreground">
              Classes held Saturday & Sunday, 3:00 PM - 5:00 PM EST at 54 St. Paul Crescent, Barrie
            </p>
          </div>
        </div>
      </section>

      {/* Monthly / Term Tuition Payment Section */}
      <section className="py-16 lg:py-24 bg-card border-t border-border" data-testid="monthly-payment-section">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeading
            badge="Enrolled Parents"
            title="Tuition Payment"
            description="Pay monthly or save with a one-time full-term payment. Choose the plan that works best for your family."
          />
          
          <MonthlyPaymentForm />
        </div>
      </section>
    </div>
  );
};

// Monthly / Term Payment Form Component
const MonthlyPaymentForm = () => {
  const [paymentPlan, setPaymentPlan] = useState('monthly'); // 'monthly' | 'term'
  const [selectedAmount, setSelectedAmount] = useState(null);
  const [selectedLabel, setSelectedLabel] = useState('');
  const [isRecurring, setIsRecurring] = useState(false);
  const [childrenNames, setChildrenNames] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);

  const pricingOptions = [
    { label: '1 Child',    monthly: 75,  term: 280 },
    { label: '2 Children', monthly: 110, term: 400 },
    { label: '3 Children', monthly: 140, term: 520 },
    { label: '4 Children', monthly: 160, term: 600 },
  ];

  // Reset selection when switching plan
  const switchPlan = (plan) => {
    setPaymentPlan(plan);
    setSelectedAmount(null);
    setSelectedLabel('');
    if (plan === 'term') setIsRecurring(false);  // no recurring for one-off term payment
  };

  const handlePayment = async () => {
    if (!selectedAmount) {
      toast.error('Please select a payment amount');
      return;
    }

    if (!childrenNames.trim()) {
      toast.error('Please enter the name(s) of your enrolled children');
      return;
    }

    setIsProcessing(true);
    try {
      const response = await axios.post(`${API}/education/tuition-payment`, {
        amount: selectedAmount,
        is_recurring: paymentPlan === 'monthly' && isRecurring,
        children_names: childrenNames,
        payment_plan: paymentPlan,
        plan_label: selectedLabel,
        origin_url: window.location.origin
      });

      if (response.data.url) {
        window.location.href = response.data.url;
      }
    } catch (error) {
      console.error('Error creating payment:', error);
      toast.error('Failed to create payment. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Plan Toggle */}
      <div className="p-2 rounded-2xl bg-background border border-border inline-flex w-full sm:w-auto" data-testid="plan-toggle">
        <button
          onClick={() => switchPlan('monthly')}
          className={`flex-1 sm:flex-initial px-6 py-2 rounded-xl text-sm font-medium transition-all ${paymentPlan === 'monthly' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground'}`}
          data-testid="plan-monthly"
        >
          Monthly
        </button>
        <button
          onClick={() => switchPlan('term')}
          className={`flex-1 sm:flex-initial px-6 py-2 rounded-xl text-sm font-medium transition-all ${paymentPlan === 'term' ? 'bg-secondary text-secondary-foreground' : 'text-muted-foreground hover:text-foreground'}`}
          data-testid="plan-term"
        >
          Full Term
        </button>
      </div>

      {/* Amount Selection */}
      <div className="p-6 rounded-2xl bg-background border border-border">
        <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
          <CreditCard className="h-5 w-5 text-primary" />
          Select {paymentPlan === 'monthly' ? 'Monthly Fee' : 'Term Payment'}
        </h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {pricingOptions.map((option) => {
            const amount = paymentPlan === 'monthly' ? option.monthly : option.term;
            const isSelected = selectedAmount === amount && selectedLabel === option.label;
            return (
              <button
                key={option.label}
                onClick={() => { setSelectedAmount(amount); setSelectedLabel(option.label); }}
                className={`p-4 rounded-xl border-2 text-center transition-all ${
                  isSelected ? 'border-primary bg-primary/10' : 'border-border hover:border-primary/50'
                }`}
                data-testid={`tuition-amount-${paymentPlan}-${option.label.replace(/\s+/g, '-').toLowerCase()}`}
              >
                <p className="text-sm text-muted-foreground">{option.label}</p>
                <p className={`text-2xl font-bold ${isSelected ? 'text-primary' : 'text-foreground'}`}>
                  ${amount}
                </p>
                <p className="text-xs text-muted-foreground">{paymentPlan === 'monthly' ? 'per month' : 'per term'}</p>
              </button>
            );
          })}
        </div>
        <p className="text-xs text-muted-foreground mt-4">
          💡 Tip: Choosing the full term is cheaper than paying monthly — {paymentPlan === 'monthly' ? 'switch to Full Term above to see term prices.' : 'save vs. paying monthly.'}
        </p>
      </div>

      {/* Recurring Payment Toggle (monthly only) */}
      {paymentPlan === 'monthly' && (
        <div className="p-6 rounded-2xl bg-background border border-border">
          <label className="flex items-center justify-between cursor-pointer" data-testid="recurring-toggle">
            <div>
              <h3 className="text-lg font-semibold text-foreground flex items-center gap-2">
                <Calendar className="h-5 w-5 text-secondary" />
                Recurring Monthly Payment
              </h3>
              <p className="text-sm text-muted-foreground mt-1">
                Automatically pay each month (you can cancel anytime)
              </p>
            </div>
            <div className="relative">
              <input
                type="checkbox"
                checked={isRecurring}
                onChange={(e) => setIsRecurring(e.target.checked)}
                className="sr-only"
              />
              <div className={`w-14 h-8 rounded-full transition-colors ${isRecurring ? 'bg-primary' : 'bg-muted'}`}>
                <div className={`w-6 h-6 rounded-full bg-white shadow-md transform transition-transform absolute top-1 ${isRecurring ? 'translate-x-7' : 'translate-x-1'}`} />
              </div>
            </div>
          </label>
          {isRecurring && (
            <div className="mt-3 p-3 rounded-lg bg-primary/10 border border-primary/20">
              <p className="text-sm text-primary">
                ✓ Your card will be charged ${selectedAmount || '—'}/month automatically
              </p>
            </div>
          )}
        </div>
      )}

      {/* Children Names */}
      <div className="p-6 rounded-2xl bg-background border border-border">
        <h3 className="text-lg font-semibold text-foreground mb-2 flex items-center gap-2">
          <Heart className="h-5 w-5 text-secondary" />
          Enrolled Children
        </h3>
        <p className="text-sm text-muted-foreground mb-4">
          Enter the name(s) of your enrolled children for our records
        </p>
        <textarea
          value={childrenNames}
          onChange={(e) => setChildrenNames(e.target.value)}
          placeholder="e.g., Ahmad Khan, Fatima Khan"
          rows={3}
          className="w-full px-4 py-3 rounded-xl bg-card border border-border focus:border-primary focus:ring-1 focus:ring-primary text-foreground resize-none"
          data-testid="children-names-input"
        />
      </div>

      {/* Payment Summary */}
      {selectedAmount && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="p-6 rounded-2xl bg-primary/5 border border-primary/20"
        >
          <h3 className="text-lg font-semibold text-foreground mb-3">Payment Summary</h3>
          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Plan</span>
              <span className="text-foreground font-medium">{selectedLabel} — {paymentPlan === 'monthly' ? 'Monthly' : 'Full Term'}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">{paymentPlan === 'monthly' ? 'Monthly Tuition' : 'Term Tuition'}</span>
              <span className="text-foreground font-medium">${selectedAmount}</span>
            </div>
            {paymentPlan === 'monthly' && (
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Payment Type</span>
                <span className={`font-medium ${isRecurring ? 'text-primary' : 'text-foreground'}`}>
                  {isRecurring ? 'Recurring Monthly' : 'One-time'}
                </span>
              </div>
            )}
            <div className="border-t border-border pt-2 mt-2">
              <div className="flex justify-between">
                <span className="text-foreground font-semibold">Total {paymentPlan === 'monthly' && isRecurring ? '(per month)' : ''}</span>
                <span className="text-primary font-bold text-xl">${selectedAmount}</span>
              </div>
            </div>
          </div>
        </motion.div>
      )}

      {/* Pay Button */}
      <Button
        onClick={handlePayment}
        disabled={isProcessing || !selectedAmount}
        className="w-full h-14 rounded-xl text-lg font-semibold bg-primary text-primary-foreground hover:bg-primary/90 glow-teal disabled:opacity-50"
        data-testid="pay-tuition-btn"
      >
        {isProcessing ? (
          <>
            <Loader2 className="mr-2 h-5 w-5 animate-spin" />
            Processing...
          </>
        ) : (
          <>
            <CreditCard className="mr-2 h-5 w-5" />
            {paymentPlan === 'monthly' && isRecurring ? 'Set Up Monthly Payment' : 'Pay Now'}
          </>
        )}
      </Button>

      <p className="text-center text-xs text-muted-foreground">
        Secure payment powered by Stripe. {paymentPlan === 'monthly' && isRecurring && 'Cancel recurring payments anytime.'}
      </p>
    </div>
  );
};

export default EducationPage;
